import superagent from 'superagent';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {normalize, schema} from 'normalizr';

import {getDefaultDecisions} from '../util/getDefaultDecisions';
import {indexConditions} from '../util/indexConditions';
import {regionToFeatureValueMap} from '../lightProfiles';

export const fetchModels = createAsyncThunk(
	'productData/models',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/${getState().status.mode}/models/${getState().status.productFamily}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchProductData = createAsyncThunk(
	'productData/productDataLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/${getState().status.mode}/productData/${getState().configuration.data.projects[0].project.featureModelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchConditionalVariantValues = createAsyncThunk(
	'productData/conditionalVariantValuesLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/${getState().status.mode}/conditionalvariantvalues/${getState().configuration.data.projects[0].project.featureModelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchTranslations = createAsyncThunk(
	'productData/translationsLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/${getState().status.mode}/translations/${getState().configuration.data.projects[0].project.featureModelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchVariantCodeTypeTemplate = createAsyncThunk(
	'productData/variantCodeTypeTemplateLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/variant-code-template-data/${getState().configuration.data.projects[0].project.featureModelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchLLMF = createAsyncThunk(
	'productData/LLMFLoading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchRelux1 = createAsyncThunk(
	'productData/Relux1Loading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchRelux2 = createAsyncThunk(
	'productData/Relux2Loading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);

export const productDataSlice = createSlice({
	name: 'productData',
	initialState: {
		status: 'idle',
		models: [],
		modelTranslations: [],
		productData: [],
		translations: [],
		conditionalVariantValues: [],
		variantCodeTypeTemplate: [],
		llmf: null,
		relux1: null,
		relux2: null,
		defaults: [],
		error: {},
	},
	reducers: {
		initDefaults: (state, action) => {
			const region = action.payload.region ?? 'int';
			state.defaults = getDefaultDecisions(state.productData, state.originalConditionalVariantValues);
			state.defaults.push(
				{
					variableId: 'CX_Region',
					featureValues: [
						{
							featureValueId: regionToFeatureValueMap[region],
							state: 'Included',
							kind: 'Explicit'
						}
					]
				},
			);
		},
		clearRelux1: state => {
			state.relux1 = null;
		},
		clearRelux2: state => {
			state.relux2 = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchModels.fulfilled, (state, action) => {
			state.models = action.payload.models;
			state.modelTranslations = normalize(
				action.payload.translations,
				[new schema.Entity('modelTranslations', undefined, {idAttribute: (e) => (`${e.languageTwoLetterCode}.${e.resourceKey.trim()}`)})]
			).entities.modelTranslations;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchProductData.fulfilled, (state, action) => {
			state.productData = action.payload;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchProductData.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchProductData.rejected, (state, action) => {
			state.data = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchConditionalVariantValues.fulfilled, (state, action) => {
			state.originalConditionalVariantValues = action.payload;
			state.conditionalVariantValues = indexConditions(action.payload);
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchConditionalVariantValues.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchConditionalVariantValues.rejected, (state, action) => {
			state.conditionalVariantValues = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchTranslations.fulfilled, (state, action) => {
			state.translations = normalize(
				action.payload,
				[new schema.Entity('translations', undefined, {idAttribute: (e) => (`${e.languageTwoLetterCode}.${e.context.trim()}.${e.resourceKey.trim()}`)})]
			).entities.translations;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchTranslations.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchTranslations.rejected, (state, action) => {
			state.translations = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchVariantCodeTypeTemplate.fulfilled, (state, action) => {
			state.variantCodeTypeTemplate = action.payload;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchVariantCodeTypeTemplate.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchVariantCodeTypeTemplate.rejected, (state, action) => {
			state.variantCodeTypeTemplate = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchLLMF.fulfilled, (state, action) => {
			state.llmf = action.payload;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchRelux1.fulfilled, (state, action) => {
			if (action.payload) {
				state.relux1 = mapReluxDownloads(action.payload);
				state.status = 'idle';
				state.error = {};
			}
		});
		builder.addCase(fetchRelux2.fulfilled, (state, action) => {
			if (action.payload) {
				state.relux2 = mapReluxDownloads(action.payload);
				state.status = 'idle';
				state.error = {};
			}
		});
	}
});

function mapReluxDownloads(payload) {
	if (payload.code && payload.code === 'ERR_BAD_RESPONSE') {
		return null;
	} else {
		return Object.entries(payload)
			.map(([key, value]) => ({type: key, url: value.url}))
			.filter(item => item.url);
	}
}

export const {initDefaults, clearRelux1, clearRelux2} = productDataSlice.actions;
export default productDataSlice.reducer;
